"use client";
import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";

interface ProgressRingProps {
    progress: number; // 0-100
    size?: number;
    strokeWidth?: number;
}

// 100%完整闭合圆环path，起点与设计稿一致
const PATH_D = "M28 4 A24 24 0 1 1 28 52 A24 24 0 1 1 28 4 Z";
// 该path的精确长度（用svg-path-properties测得）
const PATH_LENGTH = 150.8;

const ProgressRing: React.FC<ProgressRingProps> = ({ progress, size = 56, strokeWidth = 7 }) => {
    const theme = useTheme();
    const [displayProgress, setDisplayProgress] = React.useState(0);
    const gradientId = React.useId(); // 生成唯一ID避免冲突

    // 根据主题定义颜色
    const colors = {
        // 背景圆环颜色
        background: theme.palette.mode === "dark" 
            ? "#44474B"
            : "#D9DCDE",
        // 进度圆环渐变起始颜色
        gradientStart: theme.palette.mode === "dark" 
            ? "#44474B" 
            : "#44474B",
        // 进度圆环渐变结束颜色
        gradientEnd: theme.palette.mode === "dark" 
            ? "#A0A8B1" 
            : "#44474B",
        // 文字颜色使用当前颜色（继承父元素）
        text: "currentColor"
    };

    useEffect(() => {
        // 每次progress变化时，从0开始动画到目标值
        setDisplayProgress(0);
        let frame: number;
        const animate = () => {
            setDisplayProgress((prev) => {
                if (prev >= progress - 0.5) return progress;
                return prev + Math.max((progress / 30), 1); // 30帧内完成动画
            });
            if (displayProgress < progress - 0.5) {
                frame = requestAnimationFrame(animate);
            }
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [progress]);

    const dashoffset = PATH_LENGTH * (1 - displayProgress / 100);
    
    return (
        <svg width={size} height={size} viewBox="0 0 56 56" fill="none" style={{ position: 'relative', display: 'block' }}>
            <defs>
                <radialGradient
                    id={gradientId}
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(28) rotate(90) scale(38 44.7042)"
                >
                    <stop stopColor={colors.gradientStart} stopOpacity="0" />
                    <stop offset="1" stopColor={colors.gradientEnd} />
                </radialGradient>
            </defs>
            
            {/* 背景圆环 - 在进度为0时显示 */}
            <path
                d={PATH_D}
                stroke={colors.background}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                fill="none"
            />
        
            {/* 进度圆环 */}
            {displayProgress > 0 && (
                <path
                    d={PATH_D}
                    stroke={displayProgress >= 100 ? colors.gradientEnd : `url(#${gradientId})`}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={PATH_LENGTH}
                    strokeDashoffset={dashoffset}
                    style={{ transition: "stroke-dashoffset 0.7s cubic-bezier(0.4,0,0.2,1)" }}
                />
            )}
            
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="central"
                fontWeight="600"
                fontSize="var(--font-size-xs)"
                fill={colors.text}
            >
                {Math.round(displayProgress)}%
            </text>
        </svg>
    );
};

export default ProgressRing;